import { graphQlClient } from '@config/graphqlClient';
import SubscriptionGraphQL from '@graphql/subscription.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getSubscriptionsRequest, Subscription, SubscriptionStatus } from '@models/subscription.model';
import { Cancel, CheckCircle, Error as ErrorIcon, Warning } from '@mui/icons-material';
import { Box, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import React, { useEffect, useState } from 'react';

const SubscriptionsPage: React.FC = () => {
  const localeSubscriptions = useTranslation('subscriptions');
  const localeCommon = useTranslation('common');
  const dispatch = useAppDispatch();

  // Check if running in Stripe sandbox mode
  const isSandboxMode = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test');

  // Check if Stripe is enabled at all
  const isStripeEnabled = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoadingData(true);
      const data: getSubscriptionsRequest = await graphQlClient.request(
        SubscriptionGraphQL.queries.getSubscriptions,
        {
          subscriptions: {
            page: 0,
            limit: 100,
            search: '',
            filter: 'All',
            orderBy: 'createdAt',
            orderDirection: 'DESC',
          },
        },
      );
      if (data?.getSubscriptions) {
        setSubscriptions(data.getSubscriptions.subscriptions || []);
        setTotalCount(data.getSubscriptions.count || 0);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoadingData(false);
    }
  };

  const calculateMRR = () => {
    // Assuming $15 per admin seat and $2 per user seat
    return subscriptions
      .filter((sub) => sub.status === SubscriptionStatus.ACTIVE)
      .reduce((sum, sub) => sum + (sub.adminSeats * 15 + sub.userSeats * 2), 0);
  };

  const totalMRR = calculateMRR();
  const activeSubscriptions = subscriptions.filter((sub) => sub.status === SubscriptionStatus.ACTIVE).length;
  const trialSubscriptions = subscriptions.filter((sub) => sub.status === SubscriptionStatus.TRIAL).length;

  const getStatusChip = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return <Chip label={localeSubscriptions['active']} color="success" size="small" icon={<CheckCircle />} />;
      case SubscriptionStatus.TRIAL:
        return <Chip label={localeSubscriptions['trial']} color="warning" size="small" icon={<Warning />} />;
      case SubscriptionStatus.CANCELED:
        return <Chip label={localeSubscriptions['canceled']} color="default" size="small" icon={<Cancel />} />;
      case SubscriptionStatus.PAST_DUE:
        return <Chip label={localeSubscriptions['pastDue']} color="error" size="small" icon={<ErrorIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getPlanName = (subscription: Subscription) => {
    if (subscription.adminSeats === 0 && subscription.userSeats === 0) {
      return 'Free';
    }
    return 'Professional';
  };

  const getTotalSeats = (subscription: Subscription) => {
    return subscription.adminSeats + subscription.userSeats;
  };

  const getMonthlyRevenue = (subscription: Subscription) => {
    if (subscription.status !== SubscriptionStatus.ACTIVE) {
      return 0;
    }
    return subscription.adminSeats * 15 + subscription.userSeats * 2;
  };

  const formatPrice = (price: number): string => {
    // If it's a whole number, return as-is, otherwise show 2 decimal places
    return Number.isInteger(price) ? price.toString() : price.toFixed(2);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box className="flex flex-wrap w-full p-4 justify-center mx-auto">
      <Box className="w-full mb-28">
        <Typography variant="h2" className="!my-8 text-center sm:text-left">
          {localeSubscriptions['subscriptions']}
        </Typography>

        {/* No Stripe Key Notice */}
        {!isStripeEnabled && (
          <Box className="bg-error/10 border-2 border-error rounded-lg p-4 mb-6">
            <Typography variant="body14semibold" className="text-error">
              ⚠️ {localeSubscriptions['noStripeKeyNotice']}
            </Typography>
          </Box>
        )}

        {/* Sandbox Mode Indicator */}
        {isStripeEnabled && isSandboxMode && (
          <Box className="bg-warning/10 border-2 border-warning rounded-lg p-4 mb-6">
            <Typography variant="body14semibold" className="text-warning">
              ⚠️ {localeSubscriptions['sandboxMode']}
            </Typography>
          </Box>
        )}

        {/* Summary Cards */}
        <Box className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Box className="bg-white rounded-lg p-6 shadow-sm">
            <Typography variant="body12" color="textSecondary" className="mb-2">
              {localeSubscriptions['totalMRR']}
            </Typography>
            <Typography variant="h2" color="primary">
              ${formatPrice(totalMRR)}
            </Typography>
          </Box>

          <Box className="bg-white rounded-lg p-6 shadow-sm">
            <Typography variant="body12" color="textSecondary" className="mb-2">
              {localeSubscriptions['activeSubscriptions']}
            </Typography>
            <Typography variant="h2">{activeSubscriptions}</Typography>
          </Box>

          <Box className="bg-white rounded-lg p-6 shadow-sm">
            <Typography variant="body12" color="textSecondary" className="mb-2">
              {localeSubscriptions['trialSubscriptions']}
            </Typography>
            <Typography variant="h2">{trialSubscriptions}</Typography>
          </Box>
        </Box>

        {/* Subscriptions Table */}
        <Box className="bg-white rounded-lg p-6 shadow-sm">
          <Box className="mb-4">
            <Typography variant="h4semibold">
              {localeSubscriptions['allSubscriptions']} ({totalCount})
            </Typography>
          </Box>

          {loadingData ? (
            <Box className="flex justify-center items-center p-8">
              <CircularProgress size={40} />
            </Box>
          ) : subscriptions.length === 0 ? (
            <Box className="text-center py-8">
              <Typography variant="body14" color="textSecondary">
                {localeSubscriptions['noSubscriptions']}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{localeCommon['organization']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['plan']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['status']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['createdAt']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['updatedAt']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['adminSeats']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['userSeats']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['totalSeats']}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{localeSubscriptions['monthlyRevenue']}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id} hover>
                      <TableCell>
                        <Typography variant="body14semibold">
                          {subscription.organizationName || subscription.organizationId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body14">{getPlanName(subscription)}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(subscription.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body14">{formatDate(subscription.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body14">{formatDate(subscription.updatedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body14">{subscription.adminSeats}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body14">{subscription.userSeats}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body14semibold">{getTotalSeats(subscription)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body14"
                          color={getMonthlyRevenue(subscription) > 0 ? 'primary' : 'textSecondary'}>
                          ${formatPrice(getMonthlyRevenue(subscription))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SubscriptionsPage;
